import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const BloodPressureChartDialog = ({ open, onClose, vitals = [] }) => {
  // Format labels
  const labels = vitals.map((v) =>
    v.time ? `${v.date} ${v.time}` : v.date
  );

  // Extract BP readings as systolic/diastolic
  const systolic = vitals.map((v) => {
    if (!v.bp) return null;
    const [sys] = v.bp.split("/").map(Number);
    return isNaN(sys) ? null : sys;
  });

  const diastolic = vitals.map((v) => {
    if (!v.bp) return null;
    const [, dia] = v.bp.split("/").map(Number);
    return isNaN(dia) ? null : dia;
  });

  // Count valid systolic readings
  const validCount = systolic.filter((x) => x !== null).length;
  const hasInsufficientData = validCount < 2;

  const data = {
    labels,
    datasets: [
      {
        label: "Systolic (mmHg)",
        data: systolic,
        borderColor: "#d32f2f",
        backgroundColor: "rgba(211, 47, 47, 0.25)",
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: "#d32f2f",
        borderWidth: 3,
        tension: 0.3,
        spanGaps: true,
      },
      {
        label: "Diastolic (mmHg)",
        data: diastolic,
        borderColor: "#1976d2",
        backgroundColor: "rgba(25, 118, 210, 0.25)",
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: "#1976d2",
        borderWidth: 3,
        tension: 0.3,
        spanGaps: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "top",
        labels: { font: { size: 14, weight: "bold" }, padding: 15 },
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.8)",
        padding: 12,
        callbacks: {
          label: function (ctx) {
            return `${ctx.dataset.label}: ${ctx.parsed.y} mmHg`;
          },
        },
      },
    },
    scales: {
      y: {
        suggestedMin: 50,
        suggestedMax: 180,
        title: {
          display: true,
          text: "Blood Pressure (mmHg)",
          font: { size: 14, weight: "bold" },
        },
        ticks: {
          callback: (value) => `${value} mmHg`,
        },
      },
      x: {
        title: {
          display: true,
          text: "Date & Time",
          font: { size: 14, weight: "bold" },
        },
        ticks: { maxRotation: 45, minRotation: 45 },
      },
    },
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #19b934ff 0%, #19b934ff 100%)",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 700,
        }}
      >
        Blood Pressure Chart
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: 'background.default' }}>
        {vitals.length === 0 && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography fontWeight={600}>No blood pressure data available</Typography>
            <Typography variant="caption">
              Readings will appear here once data is recorded.
            </Typography>
          </Alert>
        )}

        {vitals.length > 0 && hasInsufficientData && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography fontWeight={600}>
              Not enough readings for trend visualization
            </Typography>
            <Typography variant="caption">
              At least 2 BP records are required. Current: {validCount}
            </Typography>
          </Alert>
        )}

        {vitals.length > 0 && (
          <>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                mb: 3,
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Blood Pressure Trend
              </Typography>

              <Box sx={{ minHeight: 300 }}>
                <Line data={data} options={options} />
              </Box>
            </Paper>

            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: "grey.100" }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Observations
              </Typography>

              {vitals.map((v, i) => (
                <Typography key={i} sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                  • <b>{v.bp}</b> on <b>{v.date}</b>{" "}
                  {v.time && <>at <b>{v.time}</b></>}
                </Typography>
              ))}
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BloodPressureChartDialog;
